import * as React from 'react';
import styled from 'styled-components';

import { InfoBox } from '@longline/aqua-ui/containers/InfoBox';
import { TertiaryButton } from '@longline/aqua-ui/controls/TertiaryButton';
import { NumberFormatter } from '@longline/aqua-ui/formatters/NumberFormatter';

import { useGlobalState } from '../contexts/state/GlobalStateContext';
import { IArea } from '../types/IArea';
import { AreaGridUtils } from '../util/AreaGridUtils';
import { AreaInfoBoxHeader } from './AreaInfoBox/AreaInfoBoxHeader';

interface IProps {
  value?: IArea;
}

const AreaSimpleInfoBox = (props: IProps) => {
  const globalState = useGlobalState();

  // Optional because <ListView aux> injects the active item; render nothing
  // if no item is selected.
  if (!props.value) return null;
  const area = props.value;

  const getWidth = () => AreaGridUtils.width(area.rect);
  const getHeight = () => AreaGridUtils.height(area.rect);
  const getVerticalResolution = () => AreaGridUtils.verticalResolution(area.rect, area.resolution);
  const getArea = () => getWidth() * getHeight();

  const humanLength = (name: string, lengthMeters: number) => {
    if (lengthMeters > 1000)
      return <InfoBox.Row name={<>{name} <Unit>(km)</Unit></>} value={<NumberFormatter value={lengthMeters / 1000} decimals={2} />} />;
    return <InfoBox.Row name={<>{name} <Unit>(m)</Unit></>} value={<NumberFormatter value={lengthMeters} decimals={0} />} />;
  };

  const humanArea = (name: string, aream2: number) => {
    if (aream2 > 1000000)
      return <InfoBox.Row name={<>{name} <Unit>(km²)</Unit></>} value={<NumberFormatter value={aream2 / 1000000} decimals={2} />} />;
    return <InfoBox.Row name={<>{name} <Unit>(m²)</Unit></>} value={<NumberFormatter value={aream2} decimals={0} />} />;
  };

  return (
    <InfoBox
      header={<AreaInfoBoxHeader value={area}/>}
      padded
      width={220}
      footer={<>
        <TertiaryButton onClick={() => { globalState.focusArea(area); globalState.setMode('area-editor'); }}>FOCUS</TertiaryButton>
      </>}
    >
      {humanLength("Width", getWidth())}
      {humanLength("Height", getHeight())}
      {humanArea("Area", getArea())}
      <InfoBox.Row
        name="Resolution"
        value={<><NumberFormatter value={area.resolution} decimals={0} /> × <NumberFormatter value={getVerticalResolution()} decimals={0} /></>}
      />
      {humanLength("Cell size", AreaGridUtils.cellWidth(area.rect, area.resolution))}
    </InfoBox>
  );
};

const Header = styled.div`
  font-weight: 500;
`;

const Unit = styled.span`
  display: inline-block;
  font-size: 80%;
  font-weight: normal;
  color: #eee;
`;

export { AreaSimpleInfoBox };
